export const DOWNLOAD_TRANSPORT_MODE = Object.freeze({
  AUTO: "auto",
  SINGLE: "single",
  BATCH: "batch",
  CHUNKED: "chunked"
});

const VALID_MODES = new Set(Object.values(DOWNLOAD_TRANSPORT_MODE));
const LOCAL_OVERRIDE_KEY = "dvDownloadTransportMode";
let runtimeDefaultMode = DOWNLOAD_TRANSPORT_MODE.AUTO;

/**
 * Normalizes transport mode to a supported value.
 * @param {string} mode
 * @returns {string}
 */
export const normalizeDownloadTransportMode = mode => {
  if (typeof mode !== "string") {
    return DOWNLOAD_TRANSPORT_MODE.AUTO;
  }
  const normalized = mode.trim().toLowerCase();
  return VALID_MODES.has(normalized)
    ? normalized
    : DOWNLOAD_TRANSPORT_MODE.AUTO;
};

/**
 * Sets runtime default transport mode.
 * @param {string} mode
 * @returns {string}
 */
export const setDownloadTransportRuntimeDefaultMode = mode => {
  runtimeDefaultMode = normalizeDownloadTransportMode(mode);
  return runtimeDefaultMode;
};

/**
 * Returns runtime default transport mode.
 * @returns {string}
 */
export const getDownloadTransportRuntimeDefaultMode = () => runtimeDefaultMode;

// Accept an explicit local override while ignoring malformed/stale values.
/**
 * Reads valid transport override from localStorage.
 * @returns {string|null}
 */
const readLocalOverrideMode = () => {
  if (typeof window === "undefined" || !window.localStorage) {
    return null;
  }
  try {
    const rawMode = window.localStorage.getItem(LOCAL_OVERRIDE_KEY);
    if (!rawMode) {
      return null;
    }
    const normalized = normalizeDownloadTransportMode(rawMode);
    return normalized !== DOWNLOAD_TRANSPORT_MODE.AUTO ||
      rawMode.trim().toLowerCase() === DOWNLOAD_TRANSPORT_MODE.AUTO
      ? normalized
      : null;
  } catch (_) {
    return null;
  }
};

/**
 * Returns effective transport mode considering local override policy.
 * @param {Object} [options]
 * @returns {string}
 */
export const getDefaultDownloadTransportMode = (options = {}) => {
  const allowLocalOverride = options.allowLocalOverride !== false;
  if (allowLocalOverride) {
    const override = readLocalOverrideMode();
    if (override) {
      return override;
    }
  }
  return runtimeDefaultMode;
};
